import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Drawer,
  TextField,
  FormControlLabel,
  Switch,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Dialog,
  CircularProgress,
  Grid,
  FormControl,
  InputAdornment
} from '@mui/material';
import { Edit, LockReset, Add, Search, Clear, FilterList } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { Email } from '@mui/icons-material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  disablePortal: true,
  style: {
    zIndex: 1302
  }
};

export default function Usuarios() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estado para el usuario actual
  const [currentUser, setCurrentUser] = useState(null);
  const isAdmin = currentUser?.roles?.includes('Administrador');

  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [rolesDisponibles, setRolesDisponibles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    nombre: '',
    email: '',
    rfc: '',        // <-- NUEVO: RFC separado del username
    password: '',
    activo: true,
    roles: [],
    partner: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100); // Cambiado a 100 por defecto
  const [loading, setLoading] = useState(true);

  // Filtros
  const [searchTerm, setSearchTerm] = useState(''); // B�squeda general (username/nombre/email/rfc)
  const [nombreFilter, setNombreFilter] = useState([]);
  const [nombreSearch, setNombreSearch] = useState('');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get('https://facturas.verdevalle.com.mx/api/validate-session', { withCredentials: true });
        setCurrentUser(res.data.user);
      } catch (err) {
        console.error('Error al obtener usuario actual:', err);
        navigate('/');
      }
    };

    const fetchData = async () => {
      await fetchCurrentUser();
      await Promise.all([fetchUsuarios(), fetchRoles()]);
      setLoading(false);
    };
    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (usuarios.length > 0 && currentUser) {
      let filtered = usuarios;

      // Aplicar filtros combinados
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(user =>
          (user.username && user.username.toLowerCase().includes(term)) ||
          (user.nombre && user.nombre.toLowerCase().includes(term)) ||
          (user.nombre_completo && user.nombre_completo.toLowerCase().includes(term)) ||
          (user.email && user.email.toLowerCase().includes(term)) ||
          (user.rfc && user.rfc.toLowerCase().includes(term)) // <-- b�squeda por RFC
        );
      }

      if (nombreFilter.length > 0) {
        filtered = filtered.filter(user =>
          nombreFilter.includes(user.nombre || user.nombre_completo)
        );
      }

      if (isAdmin) {
        setFilteredUsuarios(filtered);
      } else {
        // Para no-admin, muestra solo su propio usuario (comportamiento anterior)
        setFilteredUsuarios(filtered.filter(user => user.id === currentUser.id));
      }
    }
  }, [usuarios, currentUser, isAdmin, searchTerm, nombreFilter]);

  const fetchUsuarios = async () => {
    try {
      const res = await axios.get('https://facturas.verdevalle.com.mx/api/usuarios', { withCredentials: true });
      setUsuarios(res.data);
    } catch (err) {
      console.error('Error al obtener usuarios:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const res = await axios.get('https://facturas.verdevalle.com.mx/api/roles', { withCredentials: true });
      setRolesDisponibles(res.data);
    } catch (err) {
      console.error('Error al obtener roles:', err);
    } finally {
      setLoadingRoles(false);
    }
  };

  const enviarAccesos = async (user) => {
    if (!user.email || !user.username) {
      alert('El usuario no tiene correo o nombre de usuario definido.');
      return;
    }

    const confirm = window.confirm(`�Deseas enviar nuevos accesos al correo ${user.email}?`);
    if (!confirm) return;

    try {
      const res = await axios.post('https://facturas.verdevalle.com.mx/api/enviar-accesos', {
        usuario_id: user.id,
        email: user.email,
        username: user.username,
        nombre: user.nombre,
        portalUrl: process.env.REACT_APP_PORTAL_URL || 'https://facturas.verdevalle.com.mx'
      }, { withCredentials: true });

      alert(res.data.message || 'Accesos enviados correctamente.');
    } catch (err) {
      console.error('Error al enviar accesos:', err);
      alert(err.response?.data?.error || 'No se pudo enviar el correo.');
    }
  };

  const handleOpenDrawer = async (user = null) => {
    if (!isAdmin) return;

    if (rolesDisponibles.length === 0) {
      await fetchRoles();
    }

    setEditingUser(user);
    if (user) {
      const userRoleIds = rolesDisponibles
        .filter(role => user.roles.includes(role.nombre))
        .map(role => role.id);

      setFormData({
        username: user.username,
        nombre: user.nombre || user.nombre_completo,
        email: user.email,
        rfc: user.rfc || '',        // <-- prellenar RFC
        activo: Boolean(user.activo),
        roles: userRoleIds,
        password: '',
        partner: user.partner || ''
      });
    } else {
      setFormData({
        username: '',
        nombre: '',
        email: '',
        rfc: '',                    // <-- inicializar RFC
        password: '',
        activo: true,
        roles: [],
        partner: ''
      });
    }

    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingUser(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async () => {
    if (!formData.username || !formData.nombre || !formData.email) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    // Si el rol "Proveedor" está seleccionado, RFC es obligatorio
    const isProveedorSelected = rolesDisponibles.some(
      r => formData.roles.includes(r.id) && r.nombre === 'Proveedor'
    );
    if (isProveedorSelected && !formData.rfc) {
      alert('El RFC es obligatorio para usuarios con rol Proveedor');
      return;
    }

    if (!editingUser && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }

    try {
      const payload = {
        ...formData,
        activo: formData.activo ? 1 : 0
      };

      if (editingUser) {
        await axios.put(`https://facturas.verdevalle.com.mx/api/usuarios/${editingUser.id}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post('https://facturas.verdevalle.com.mx/api/usuarios', payload, {
          withCredentials: true
        });
      }

      fetchUsuarios();
      handleCloseDrawer();
    } catch (err) {
      console.error('Error al guardar usuario:', err);
      alert(err.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      alert('Por favor ingrese una nueva contraseña');
      return;
    }

    try {
      await axios.post('https://facturas.verdevalle.com.mx/api/reset-password', {
        usuario_id: resetUser.id,
        nueva_password: newPassword
      }, { withCredentials: true });

      setOpenResetDialog(false);
      setResetUser(null);
      setNewPassword('');
      alert('Contraseña actualizada correctamente');
    } catch (err) {
      console.error('Error al resetear contraseña:', err);
      alert('Error al actualizar la contraseña');
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setSearchTerm('');
    setNombreFilter([]);
    setNombreSearch('');
    setPage(0);
  };

  const renderUserForm = () => {
    const isProveedorSelected = rolesDisponibles.some(
      r => formData.roles.includes(r.id) && r.nombre === 'Proveedor'
    );

    return (
      <>
        <TextField
          label="Usuario *"
          fullWidth
          margin="normal"
          value={formData.username}
          onChange={(e) => handleFormChange('username', e.target.value)}
          error={!formData.username}
          helperText={!formData.username ? 'Campo obligatorio' : ''}
          size="small"
        />

        <TextField
          label="Nombre *"
          fullWidth
          margin="normal"
          value={formData.nombre}
          onChange={(e) => handleFormChange('nombre', e.target.value)}
          error={!formData.nombre}
          helperText={!formData.nombre ? 'Campo obligatorio' : ''}
          size="small"
        />

        <TextField
          label="Correo electrónico *"
          fullWidth
          margin="normal"
          value={formData.email}
          onChange={(e) => handleFormChange('email', e.target.value)}
          error={!formData.email}
          helperText={!formData.email ? 'Campo obligatorio' : ''}
          size="small"
        />

        <TextField
          label="RFC del proveedor"
          fullWidth
          margin="normal"
          value={formData.rfc}
          onChange={(e) => handleFormChange('rfc', e.target.value.toUpperCase())}
          inputProps={{ maxLength: 13 }}
          required={isProveedorSelected}
          error={isProveedorSelected && !formData.rfc}
          helperText={isProveedorSelected && !formData.rfc ? 'Obligatorio para rol Proveedor' : ''}
          size="small"
        />

        <TextField
          label="Partner"
          fullWidth
          margin="normal"
          value={formData.partner}
          onChange={(e) => handleFormChange('partner', e.target.value)}
          size="small"
        />

        {!editingUser && (
          <TextField
            label="Contraseña *"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={(e) => handleFormChange('password', e.target.value)}
            error={!formData.password}
            helperText={!formData.password ? 'Campo obligatorio' : ''}
            size="small"
          />
        )}

        <InputLabel id="roles-label" sx={{ mt: 2, fontSize: '0.875rem' }}>Perfiles</InputLabel>
        {loadingRoles ? (
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Cargando perfiles...</Typography>
        ) : (
          <Select
            labelId="roles-label"
            multiple
            value={formData.roles}
            onChange={(e) => {
              const value = e.target.value;
              handleFormChange('roles', typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Perfiles" size="small" />}
            renderValue={(selected) => {
              const selectedRoles = rolesDisponibles
                .filter(role => selected.includes(role.id))
                .map(role => role.nombre);
              return selectedRoles.join(', ') || 'Seleccione perfiles';
            }}
            fullWidth
            MenuProps={MenuProps}
            size="small"
          >
            {rolesDisponibles.map((rol) => (
              <MenuItem key={rol.id} value={rol.id} sx={{ fontSize: '0.75rem' }}>
                <Checkbox checked={formData.roles.indexOf(rol.id) > -1} size="small" />
                <ListItemText primary={rol.nombre} sx={{ '& span': { fontSize: '0.75rem' } }} />
              </MenuItem>
            ))}
          </Select>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formData.activo}
              onChange={(e) => handleFormChange('activo', e.target.checked)}
              size="small"
            />
          }
          label="Usuario activo"
          sx={{ mt: 2 }}
        />

        <DialogActions sx={{ mt: 3 }}>
          <Button onClick={handleCloseDrawer} size="small">Cancelar</Button>
          <Button variant="contained" onClick={handleSaveUser} size="small">
            {editingUser ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Obtener nombres �nicos para el filtro
  const uniqueNombres = [...new Set(usuarios.map(user => user.nombre || user.nombre_completo).filter(Boolean))];
  const filteredNombres = uniqueNombres.filter(nombre =>
    nombre.toLowerCase().includes(nombreSearch.toLowerCase())
  );

  return (
    <Box sx={{
      width: '100%',
      backgroundColor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1,
      p: 1,
      overflow: 'hidden'
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mb: 2
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          Gestión de Usuarios
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{ fontSize: '0.75rem' }}
          >
            Volver a Facturas
          </Button>

          {isAdmin && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={() => handleOpenDrawer()}
              sx={{ fontSize: '0.75rem' }}
            >
              Nuevo Usuario
            </Button>
          )}
        </Box>
      </Box>

      <Paper elevation={0} sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 3,
        backgroundColor: '#fff',
        boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)',
        border: '1px solid #eef2f7'
      }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por Usuario, Nombre, Email o RFC"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.75rem'
                }
              }}
            />
          </Grid>

          <Grid item xs={10} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.75rem' }}>Filtrar por Nombre</InputLabel>
              <Select
                multiple
                value={nombreFilter}
                onChange={(e) => setNombreFilter(e.target.value)}
                label="Filtrar por Nombre"
                renderValue={(selected) => selected.join(', ')}
                sx={{ fontSize: '0.75rem' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300
                    }
                  }
                }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar nombre..."
                    value={nombreSearch}
                    onChange={(e) => setNombreSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      mb: 1,
                      '& .MuiInputBase-input': {
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Box>
                <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                  <em>Seleccione nombres</em>
                </MenuItem>
                {filteredNombres.map(nombre => (
                  <MenuItem key={nombre} value={nombre} sx={{ fontSize: '0.75rem' }}>
                    <Checkbox checked={nombreFilter.includes(nombre)} size="small" />
                    <ListItemText primary={nombre} sx={{ '& span': { fontSize: '0.75rem' } }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={2} sm={1}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              onClick={clearAllFilters}
              startIcon={<FilterList fontSize="small" />}
              sx={{
                height: '40px',
                fontSize: '0.7rem',
                minWidth: 'auto'
              }}
              aria-label="Limpiar filtros"
            >
              {isMobile ? '' : 'Limpiar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{
        border: '1px solid #eef2f7',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#fff',
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#f3f4f6',
          color: '#111827',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: 0.2,
          borderBottom: '1px solid #e5e7eb',
          minHeight: 44,
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            textTransform: 'uppercase'
          }
        }}}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>RFC</TableCell> {/* <-- NUEVO */}
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsuarios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{user.username}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{user.nombre || user.nombre_completo}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{user.email}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{user.rfc || '-'}</TableCell> {/* <-- NUEVO */}
                  <TableCell sx={{ fontSize: '0.75rem' }}>{user.partner || '-'}</TableCell>
                  <TableCell>
                    {user.roles.map((rol, i) => (
                      <Chip
                        key={i}
                        label={rol}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          fontSize: '0.65rem',
                          height: '20px'
                        }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.activo ? 'Activo' : 'Inactivo'}
                      color={user.activo ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isAdmin && (
                      <Tooltip title="Editar usuario">
                        <IconButton onClick={() => handleOpenDrawer(user)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Enviar accesos por correo">
                      <IconButton onClick={() => enviarAccesos(user)} size="small">
                        <Email fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Resetear contraseña">
                      <IconButton onClick={() => { setResetUser(user); setOpenResetDialog(true); }} size="small">
                        <LockReset fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsuarios.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.75rem'
            }
          }}
        />
      </Paper>

      {isMobile ? (
        <Dialog fullScreen open={openDrawer} onClose={handleCloseDrawer}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </Typography>
            {renderUserForm()}
          </Box>
        </Dialog>
      ) : (
        <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer} sx={{ zIndex: 1301 }}>
          <Box sx={{ width: 400, p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </Typography>
            {renderUserForm()}
          </Box>
        </Drawer>
      )}

      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle sx={{ fontSize: '0.875rem' }}>Resetear contraseña para {resetUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nueva contraseña *"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 1 }}
            error={!newPassword}
            helperText={!newPassword ? 'Campo obligatorio' : ''}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)} size="small">Cancelar</Button>
          <Button variant="contained" onClick={handleResetPassword} size="small">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
